import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { DateInput, FormField, Input, Modal, Select } from "ui";

import {
  CompanySla,
  CompanySlaStatus,
  useUpsertCompanySla
} from "../../../apis";

import styles from "./CompanySlaModal.module.scss";

const FORM_ID = "company-sla-form";

interface FormValues {
  title: string;
  version: string;
  counterparty: string;
  contractNumber: string;
  status: CompanySlaStatus;
  effectiveFrom: Date | null;
  effectiveTo: Date | null;
  guaranteedDelivery: string;
  successRate: string;
  penaltyPerBreach: string;
}

const STATUS_OPTIONS = [
  { value: CompanySlaStatus.Active, label: "Active" },
  { value: CompanySlaStatus.Inactive, label: "Inactive" },
  { value: CompanySlaStatus.Pending, label: "Pending" }
];

interface CompanySlaModalProps {
  isOpen: boolean;
  companyId: string;
  sla?: CompanySla | null;
  onClose: () => void;
}

const toIsoDate = (d: Date | null): string | null => {
  if (!d) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const numOrNull = (v: string): number | null => {
  if (v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const CompanySlaModal = ({
  isOpen,
  companyId,
  sla,
  onClose
}: CompanySlaModalProps) => {
  const { t } = useTranslation();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: {
      title: "",
      version: "",
      counterparty: "",
      contractNumber: "",
      status: CompanySlaStatus.Active,
      effectiveFrom: null,
      effectiveTo: null,
      guaranteedDelivery: "",
      successRate: "",
      penaltyPerBreach: ""
    }
  });

  useEffect(() => {
    if (!isOpen) return;
    reset({
      title: sla?.title ?? "",
      version: sla?.version ?? "",
      counterparty: sla?.counterparty ?? "",
      contractNumber: sla?.contractNumber ?? "",
      status: sla?.status ?? CompanySlaStatus.Active,
      effectiveFrom: sla?.effectiveFrom ? new Date(sla.effectiveFrom) : null,
      effectiveTo: sla?.effectiveTo ? new Date(sla.effectiveTo) : null,
      guaranteedDelivery: sla?.guaranteedDelivery ?? "",
      successRate: sla?.successRate != null ? String(sla.successRate) : "",
      penaltyPerBreach:
        sla?.penaltyPerBreach != null ? String(sla.penaltyPerBreach) : ""
    });
  }, [isOpen, sla, reset]);

  const { mutate, isLoading } = useUpsertCompanySla(companyId, {
    onSuccess: () => {
      toast.success(t("Saved"));
      onClose();
    },
    onError: (e) => toast.error(e?.message || t("Failed to save"))
  });

  const onSubmit = (values: FormValues) => {
    mutate({
      title: values.title,
      version: values.version || null,
      counterparty: values.counterparty || null,
      contractNumber: values.contractNumber || null,
      status: values.status,
      effectiveFrom: toIsoDate(values.effectiveFrom),
      effectiveTo: toIsoDate(values.effectiveTo),
      guaranteedDelivery: values.guaranteedDelivery || null,
      successRate: numOrNull(values.successRate),
      penaltyPerBreach: numOrNull(values.penaltyPerBreach)
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("Service Level Agreement")}
      formId={FORM_ID}
      okText={t("Save")}
      cancelText={t("Cancel")}
      isLoading={isLoading}
      className={styles.modal}
    >
      <form
        id={FORM_ID}
        className={styles.form}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className={styles.form__row}>
          <FormField
            label={t("Agreement title")}
            errorMessage={errors.title?.message}
          >
            <Input
              {...register("title", { required: t("Required field") })}
              placeholder={t("e.g. Master Services Agreement")}
            />
          </FormField>
          <FormField
            label={t("Version")}
            errorMessage={errors.version?.message}
          >
            <Input {...register("version")} placeholder={t("e.g. v3.2")} />
          </FormField>
        </div>

        <FormField
          label={t("Counterparty")}
          errorMessage={errors.counterparty?.message}
        >
          <Input
            {...register("counterparty")}
            placeholder={t("e.g. Orange Côte d'Ivoire")}
          />
        </FormField>

        <FormField
          label={t("Contract number")}
          errorMessage={errors.contractNumber?.message}
        >
          <Input
            {...register("contractNumber")}
            placeholder={t("e.g. MSA-2025-0118")}
          />
        </FormField>

        <Controller
          control={control}
          name="status"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <FormField label={t("Status")} errorMessage={error?.message}>
              <Select
                value={value}
                placeholder={t("Select...")}
                options={STATUS_OPTIONS.map((o) => ({
                  label: t(o.label),
                  value: o.value
                }))}
                onChange={(next) =>
                  onChange(Array.isArray(next) ? next[0] : next)
                }
              />
            </FormField>
          )}
        />

        <div className={styles.form__row}>
          <Controller
            control={control}
            name="effectiveFrom"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DateInput
                value={value}
                onChange={onChange}
                label={t("Effective from")}
                errorMessage={error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="effectiveTo"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DateInput
                value={value}
                onChange={onChange}
                label={t("Effective to")}
                errorMessage={error?.message}
              />
            )}
          />
        </div>

        <FormField
          label={t("Guaranteed delivery")}
          errorMessage={errors.guaranteedDelivery?.message}
        >
          <Input
            {...register("guaranteedDelivery")}
            placeholder={t("e.g. ≤ 24h urban / ≤ 48h regional")}
          />
        </FormField>

        <div className={styles.form__row}>
          <FormField
            label={t("Success rate (%)")}
            errorMessage={errors.successRate?.message}
          >
            <Input
              type="number"
              {...register("successRate")}
              placeholder="95"
            />
          </FormField>
          <FormField
            label={t("Penalty per breach (FCFA)")}
            errorMessage={errors.penaltyPerBreach?.message}
          >
            <Input
              type="number"
              {...register("penaltyPerBreach")}
              placeholder="2500"
            />
          </FormField>
        </div>
      </form>
    </Modal>
  );
};

export default CompanySlaModal;
